import { useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";

import useCart from "../../hooks/services/useCart.hook";
import { CustomCartItem } from "../../types/cart";
import { CustomProductData } from "../../types/posProduct";
import { AttachProduct, Product } from "../../types/product";
import { PosModal } from "../reuseables/modal.comp";
import CustomProduct from "./CustomProduct.comp";
import { useTranslation } from "../../core/translation.core";

interface AttacheProductProps {
  parentProduct: Product;
  attachProducts: AttachProduct[];
  attachCartItemId: string;
  setShowAttachProduct: Dispatch<SetStateAction<boolean>>;
}

export default function AttachedProduct({
  parentProduct,
  attachProducts,
  attachCartItemId,
  setShowAttachProduct,
}: AttacheProductProps) {
  const { t, msg } = useTranslation();
  const [attachProduct, setAttachProduct] = useState(attachProducts[0].product);
  const handleSelectProduct = (p: Product) => setAttachProduct(p);

  function getHeader(productType: "simple" | "custom" | "variationParent" | "variationChild") {
    switch (productType) {
      case "simple":
        return t(msg.product.simple);
      case "custom":
        return t(msg.product.customProduct);
      case "variationParent":
        return t(msg.product.variationProduct);
      default:
        return t(msg.product.customProduct);
    }
  }

  return (
    <PosModal setModalOpened={setShowAttachProduct} btnRef={null as any} variant="LARGE">
      <section className="flex flex-col items-center w-4/5 mx-auto min-h-[400px]">
        <div className="self-start mb-6">
          <h2 className="text-2xl md:text-3xl">{getHeader(attachProduct.productType)}</h2>
          <p>
            {t(msg.product.attachProductTo)} <span className="text-blue-400">{parentProduct.name}</span>?
          </p>
        </div>
        <AttachProductList
          attachProducts={attachProducts}
          selectedProductId={attachProduct.id}
          handleClick={handleSelectProduct}
        />
        <ProductSelector
          attachProduct={attachProduct}
          attachCartItemId={attachCartItemId}
          onProductAdded={() => setShowAttachProduct(false)}
        />
      </section>
    </PosModal>
  );
}

interface AttachedProductListProps {
  attachProducts: AttachProduct[];
  selectedProductId: string;
  handleClick: (product: Product) => void;
}

const AttachProductList = ({ attachProducts, selectedProductId, handleClick }: AttachedProductListProps) => {
  const { t, msg } = useTranslation();

  const handleSelect = (id: string) => {
    const selectedProduct = attachProducts.find((ap) => ap.product.id === id);
    handleClick((selectedProduct as any).product);
  };

  return (
    <label className="bg-inherit w-full mb-4 text-xl lg:mb-6">
      {t(msg.posMenuItems.selectProduct)}
      <select
        className="px-3 py-2 w-full rounded border border-[#CCCCCC] text-black dark:text-dark-gray-text dark:bg-pos-black-blue"
        onChange={(e) => handleSelect(e.target.value)}
        value={selectedProductId}
      >
        {attachProducts?.map((ap) => (
          <option key={ap.product.id} value={ap.product.id}>
            {ap.product.name}
          </option>
        ))}
      </select>
    </label>
  );
};

interface ProductSelectorProps {
  attachProduct: Product;
  attachCartItemId: string;
  onProductAdded: VoidFunction;
}
const ProductSelector = ({ attachProduct, attachCartItemId, onProductAdded }: ProductSelectorProps) => {
  switch (attachProduct.productType) {
    case "custom":
      return <CustomProduct customProduct={attachProduct} attachCartItemId={attachCartItemId} onProductAdded={onProductAdded} />;

    case "variationParent":
      return <p>TODO: Variation ProductSelector</p>;
    default:
      return <p>TODO: Simple ProductSelector</p>;
  }
};
